/**
 * Tournament Panel Component (Campaign Variant)
 * Displays tournament info in panel format within the campaign system.
 * Follows the same pattern as PrizePopPanel.
 */

import { type FC, useCallback } from 'react';
import {
  useSelectedCampaignId,
  useCampaignDataAttributes,
  useCampaignCardScale,
  useAutoJoin,
  TOURNAMENTS_CAMPAIGN_TYPE,
} from 'features/campaigns';
import JoinInButton from '../../../shared/JoinIn/JoinInButton';
import PrizeFundCampaign from '../../../shared/PrizeFundCampaign/PrizeFundCampaign';
import StartsIn from '../../../shared/StartsIn/StartsIn';
import PeriodDisplay from '../../../shared/PeriodDisplay/PeriodDisplay';
import { setViewMode, setSelectedSection } from 'store/app/appStore';
import { SECTION_NAMES } from '@repo/common';
import { SINGLE_VIEW_MODE } from '../../../../../../constants';
import { useCampaignStatus } from '../../../../hooks/data/campaignStatus/useCampaignStatus';
import type { CampaignPanelInfo } from '../../../MultiViewCampaignSection/types';

import styles from './TournamentPanel.module.css';

interface TournamentPanelProps {
  panelInfo: CampaignPanelInfo;
}

const TournamentPanel: FC<TournamentPanelProps> = ({ panelInfo }) => {
  const { backgroundOverlayImage, backgroundOverlayLogo = '', headerTitle, prizeFund, prizeCurrency, joined, startDate = '', endDate = '', isSingleItem = false } = panelInfo;
  const dataAttributes = useCampaignDataAttributes({
    campaignType: TOURNAMENTS_CAMPAIGN_TYPE,
    additionalProps: { 'data-variant': 'panel' }
  });

  const selectedTournamentId = useSelectedCampaignId(TOURNAMENTS_CAMPAIGN_TYPE) as string;

  const autoJoin = useAutoJoin(TOURNAMENTS_CAMPAIGN_TYPE);
  const { isFinished } = useCampaignStatus(selectedTournamentId);
  const showJoinButton = !!selectedTournamentId && !autoJoin && isSingleItem && (!isFinished || joined);

  const { titleRef, scale } = useCampaignCardScale(headerTitle, {
    recalculateKey: isSingleItem,
  });

  const handleClick = useCallback(() => {
    setSelectedSection(SECTION_NAMES.TOURNAMENTS);
    setViewMode(SINGLE_VIEW_MODE);
  }, []);

  return (
    <div
      className={styles.panelContainer}
      style={{ backgroundImage: `url(${backgroundOverlayImage})` }}
      {...dataAttributes}
    >
      {backgroundOverlayLogo && 
        <div
          className={styles.tournamentsOverlayLogo}
          style={{ backgroundImage: `url(${backgroundOverlayLogo})` }}
        />
      }
      {showJoinButton && <JoinInButton campaignId={selectedTournamentId} variant="panel" />}
      <div className={styles.centerInfoWrapper} {...dataAttributes}>
        <span
          className={styles.campaignInfoTitle}
          ref={titleRef as React.RefObject<HTMLSpanElement>}
          style={{
            transform: scale < 1 ? `scale(${scale})` : undefined,
          }}
        >
          {headerTitle}
        </span>
        <PrizeFundCampaign
          amount={prizeFund || 0}
          currency={prizeCurrency ?? ''}
          variant="panel"
        />
      </div>

      <div
        className={styles.periodWrapper}
        onClick={handleClick}
        {...dataAttributes}
      >
        {isSingleItem && !isFinished
          ? <StartsIn />
          : <>
            <div className={styles.separator} />
            <PeriodDisplay startDate={startDate} endDate={endDate} isFinished={isFinished && isSingleItem} />
          </>
        }
      </div>
    </div>
  );
};

export default TournamentPanel;
